import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme, InputLabel } from "@mui/material";
import "./styles.css";

const MONTH_EMOJIS = ["❄️", "💝", "🌸", "🌧️", "🌻", "☀️", "🔥", "🏖️", "🍂", "🎃", "🦃", "🎄"];

const WEEKDAYS = [
  { label: "Dom", weekend: true },
  { label: "Seg", weekend: false },
  { label: "Ter", weekend: false },
  { label: "Qua", weekend: false },
  { label: "Qui", weekend: false },
  { label: "Sex", weekend: false },
  { label: "Sáb", weekend: true },
];

interface SysDateRangePickerProps {
  label?: string;
  onChange?: (range: { startDate: Date | null; endDate: Date | null }) => void;
  value?: { startDate: Date | null; endDate: Date | null };
}

const SysDateRangePicker: React.FC<SysDateRangePickerProps> = ({ label, onChange, value }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(value?.startDate || null);
  const [endDate, setEndDate] = useState<Date | null>(value?.endDate || null);
  const [tempDate, setTempDate] = useState<Date>(() => {
    if (value?.startDate) {
      return new Date(value.startDate.getFullYear(), value.startDate.getMonth(), 1);
    }
    return new Date();
  });
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // Sincronizar valores quando a prop value mudar
  useEffect(() => {
    if (value !== undefined) {
      setStartDate(value.startDate);
      setEndDate(value.endDate);
      if (value.startDate) {
        setTempDate(new Date(value.startDate.getFullYear(), value.startDate.getMonth(), 1));
      }
    }
  }, [value]);

  // Fechar ao clicar fora e ao redimensionar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      const calendarEl = document.getElementById("sysdrp-portal");
      const container = ref.current;
      if (
        container &&
        !container.contains(e.target as Node) &&
        calendarEl &&
        !calendarEl.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    
    const handleResizeScroll = () => {
      if (isOpen) {
        updatePortalPosition();
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResizeScroll);
    window.addEventListener("scroll", handleResizeScroll, true);
    
    // Usar requestAnimationFrame para atualizar suavemente durante o scroll
    let rafId: number | null = null;
    const handleScroll = () => {
      if (!isOpen) return;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePortalPosition();
      });
    };
    
    window.addEventListener("scroll", handleScroll, true);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResizeScroll);
      window.removeEventListener("scroll", handleResizeScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen]);

  // Recalcular posição após o calendário ser renderizado
  useEffect(() => {
    if (isOpen) {
      // Pequeno delay para garantir que o DOM foi atualizado
      const timer = setTimeout(() => {
        updatePortalPosition();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen, tempDate]);

  const updatePortalPosition = () => {
    const container = ref.current;
    if (!container) return;
    const input = container.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();
    
    // Tentar calcular altura real do calendário se já estiver renderizado
    let calendarHeight = 380; // Altura estimada para dois meses lado a lado
    const portalEl = document.getElementById("sysdrp-portal");
    if (portalEl) {
      const calendarEl = portalEl.querySelector(".calendar");
      if (calendarEl) {
        calendarHeight = calendarEl.getBoundingClientRect().height + 20; // Adicionar margem
      }
    }
    
    // Calcular espaço disponível abaixo e acima
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const padding = 10; // Padding mínimo da tela
    const minSpace = padding + 8; // Espaço mínimo necessário
    
    // Usar getBoundingClientRect para posição relativa à viewport
    // Isso faz o calendário acompanhar o campo durante o scroll
    let top: number;
    let left = rect.left; // Não usar scrollX, usar posição relativa à viewport
    
    if (spaceBelow < calendarHeight + minSpace && spaceAbove > spaceBelow) {
      // Posicionar acima do campo (tem mais espaço acima)
      top = rect.top - calendarHeight - 8;
      // Se ainda assim sair da tela, ajustar para o topo
      if (top < padding) {
        top = padding;
      }
    } else if (spaceBelow < calendarHeight + minSpace) {
      // Não cabe nem acima nem abaixo bem, posicionar na melhor posição possível
      const spaceTotal = spaceBelow + spaceAbove;
      if (spaceTotal < calendarHeight) {
        // Não cabe completamente, centralizar o máximo possível
        top = rect.top - (calendarHeight - spaceBelow) / 2;
      } else {
        // Cabe, mas precisa ser ajustado
        top = rect.bottom + 8;
      }
      // Garantir que não saia da tela
      if (top < padding) {
        top = padding;
      }
      if (top + calendarHeight > window.innerHeight - padding) {
        top = window.innerHeight - calendarHeight - padding;
      }
    } else {
      // Posicionar abaixo do campo (padrão)
      top = rect.bottom + 8;
    }
    
    // Ajustar horizontalmente se o calendário sair da tela
    let calendarWidth = 600;
    if (portalEl) {
      const calendarEl = portalEl.querySelector(".calendar");
      if (calendarEl) {
        calendarWidth = calendarEl.getBoundingClientRect().width;
      }
    }
    if (left + calendarWidth > window.innerWidth - padding) {
      left = window.innerWidth - calendarWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }
    
    setPortalPos({ top, left, width: rect.width });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const handleSelectDate = (newDate: Date) => {

    if (!startDate || (startDate && endDate)) {
      setStartDate(newDate);
      setEndDate(null);
    } else if (newDate >= startDate) {
      setEndDate(newDate);
      setIsOpen(false);
      onChange?.({ startDate, endDate: newDate });
    } else {
      setStartDate(newDate);
      setEndDate(null);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isSameDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const isInRange = (currentDate: Date) => {
    return !!(startDate && endDate && currentDate > startDate && currentDate < endDate);
  };

  const isInPreview = (currentDate: Date) => {
    if (!startDate || endDate || !hoveredDate) return false;
    const a = startDate;
    const b = hoveredDate;
    const min = a < b ? a : b;
    const max = a < b ? b : a;
    return currentDate > min && currentDate < max;
  };

  const renderMonthDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = getDaysInMonth(month, year);

    const days: JSX.Element[] = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${year}-${month}-${i}`} className="day empty"></div>);
    }

    for (let d = 1; d <= totalDays; d++) {
      const currentDate = new Date(year, month, d);
      const selected = isSameDay(currentDate, startDate) || isSameDay(currentDate, endDate);
      const inRange = isInRange(currentDate);
      const isToday = isSameDay(currentDate, new Date());
      const inPreview = isInPreview(currentDate);

      days.push(
        <div
          key={`${year}-${month}-${d}`}
          className={`day ${selected ? "selected" : ""} ${inRange ? "in-range" : ""} ${inPreview ? "preview" : ""} ${isToday ? "today" : ""}`}
          onClick={() => handleSelectDate(currentDate)}
          onMouseEnter={() => {
            if (startDate && !endDate) setHoveredDate(currentDate);
          }}
        >
          {d}
        </div>
      );
    }

    return days;
  };

  const labelTop = 0;
  const calendarThemeStyle = {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    ["--drp-primary" as string]: theme.palette.primary.main,
    ["--drp-primary-dark" as string]: theme.palette.primary.dark,
    ["--drp-primary-soft" as string]: `${theme.palette.primary.main}22`,
    ["--drp-primary-soft-hover" as string]: `${theme.palette.primary.main}33`,
    ["--drp-surface-top" as string]: theme.palette.background.paper,
    ["--drp-surface-bottom" as string]: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc",
    ["--drp-divider" as string]: theme.palette.divider,
    ["--drp-header-bg" as string]: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    ["--drp-hover" as string]: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
  } as React.CSSProperties;

  const formatMonthLabel = (date: Date) => {
    const label = date.toLocaleString("pt-BR", { month: "long", year: "numeric" });
    return `${MONTH_EMOJIS[date.getMonth()]} ${label}`;
  };

  return (
    <div
      className="sys-date-range-picker"
      ref={ref}
      style={{
        width: '100%',
        paddingTop: label ? labelTop : 0,
      }}
    >
      {label && (
        <InputLabel
          shrink
          sx={{
            position: 'absolute',
            top: labelTop,
            left: 14,
            transform: 'translateY(-50%)',
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 400,
            backgroundColor: theme.palette.background.paper,
            padding: '0 4px',
            zIndex: 1,
          }}
        >
          {label}
        </InputLabel>
      )}
      <div className="sys-date-range-picker__field">
        <span className="sys-date-range-picker__icon" aria-hidden="true">📅</span>
        <input
          type="text"
          style={{
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
            ["--drp-primary-soft" as string]: `${theme.palette.primary.main}22`,
          }}
          readOnly
          value={
            startDate && endDate
              ? `📆 ${formatDate(startDate)} → ${formatDate(endDate)}`
              : ""
          }
          placeholder="📆 Selecione o período"
          onClick={() => {
            const next = !isOpen;
            setIsOpen(next);
            if (next) {
              updatePortalPosition();
            }
          }}
        />
      </div>
      {(startDate || endDate) && (
        <button
          type="button"
          className="clear-btn"
          aria-label="Limpar datas"
          title="Limpar datas"
          style={{
            color: theme.palette.text.secondary
          }}
          onClick={(e) => {
            e.stopPropagation();
            setStartDate(null);
            setEndDate(null);
            setHoveredDate(null);
            onChange?.({ startDate: null, endDate: null });
          }}
        >
          🧹
        </button>
      )}

      {isOpen &&
        createPortal(
          <div
            id="sysdrp-portal"
            className="calendar-portal"
            style={{ 
              top: `${portalPos.top}px`, 
              left: `${portalPos.left}px`,
              position: 'fixed',
              zIndex: 9999
            }}
          >
            <div 
              className="calendar" 
              onMouseLeave={() => setHoveredDate(null)}
              style={calendarThemeStyle}
            >
              <div className="months">
                {(() => {
                  const y1 = tempDate.getFullYear();
                  const m1 = tempDate.getMonth();
                  const next = new Date(y1, m1 + 1, 1);
                  const y2 = next.getFullYear();
                  const m2 = next.getMonth();
                  const month1Label = formatMonthLabel(tempDate);
                  const month2Label = formatMonthLabel(next);
                  return (
                    <>
                      <div className="month">
                        <div className="month-header">
                          <button
                            type="button"
                            className="month-header-btn"
                            aria-label="Mês anterior"
                            onClick={() =>
                              setTempDate(
                                new Date(tempDate.getFullYear(), tempDate.getMonth() - 1, 1)
                              )
                            }
                          >
                            ⬅️
                          </button>
                          <span className="month-header-title">{month1Label}</span>
                          <span className="month-header-spacer" aria-hidden="true" />
                        </div>
                        <div className="weekdays">
                          {WEEKDAYS.map((d) => (
                            <div
                              key={`w1-${d.label}`}
                              className={`weekday ${d.weekend ? "weekday--weekend" : ""}`}
                              style={{ color: d.weekend ? undefined : theme.palette.text.secondary }}
                            >
                              {d.label}
                            </div>
                          ))}
                        </div>
                        <div className="days">{renderMonthDays(y1, m1)}</div>
                      </div>
                      <div className="month">
                        <div className="month-header">
                          <span className="month-header-spacer" aria-hidden="true" />
                          <span className="month-header-title">{month2Label}</span>
                          <button
                            type="button"
                            className="month-header-btn"
                            aria-label="Próximo mês"
                            onClick={() =>
                              setTempDate(
                                new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, 1)
                              )
                            }
                          >
                            ➡️
                          </button>
                        </div>
                        <div className="weekdays">
                          {WEEKDAYS.map((d) => (
                            <div
                              key={`w2-${d.label}`}
                              className={`weekday ${d.weekend ? "weekday--weekend" : ""}`}
                              style={{ color: d.weekend ? undefined : theme.palette.text.secondary }}
                            >
                              {d.label}
                            </div>
                          ))}
                        </div>
                        <div className="days">{renderMonthDays(y2, m2)}</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SysDateRangePicker;
